import { cn } from '@/lib/utils';
import type { PillView, PillId } from '../../lib/kindMap';

interface ActivityFilterPillsProps {
  pills: PillView[];
  selected: PillId;
  onSelect: (pill: PillView) => void;
}

/**
 * The four filter pills, built from the server's WHOLE-RECORD kind counts.
 *
 * Two properties matter and neither is visible in the markup:
 *
 * 1. **The counts are for the whole record, not the loaded page.** A picklist produced 49 rows in one day
 *    against a page size of 25 — counting what is loaded would show a number that changed as the user
 *    scrolled, which reads as a bug even though nothing is wrong.
 * 2. **A pill whose kinds do not occur on this record is absent, not greyed at zero.** That rule lives in
 *    `buildPills`, not here; this component renders whatever it is handed. "An empty filter is worse than no
 *    filter" — the design says so, and a row of dead zeroes is exactly that.
 *
 * Selecting a pill RE-QUERIES rather than filtering the loaded page, so the result is the whole record's
 * matching rows and not just the ones already fetched.
 */
export function ActivityFilterPills({ pills, selected, onSelect }: ActivityFilterPillsProps) {
  // One pill is not a choice. A record whose rows are all one kind renders "All 6" beside nothing, which
  // costs a row of vertical space in a 364px panel to say nothing at all.
  if (pills.length <= 1) return null;

  return (
    <div
      role="group"
      aria-label="Filter activity by kind"
      className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2.5"
    >
      {pills.map((pill) => {
        const isSelected = pill.id === selected;

        return (
          <button
            key={pill.id}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(pill)}
            className={cn(
              'flex h-[25px] items-center gap-1.5 rounded-full px-2.5 text-xs transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isSelected
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            <span>{pill.label}</span>
            {/* tabular-nums so the counts do not jitter the pill widths as a filter re-queries. */}
            <span className="tabular-nums opacity-70">{pill.count}</span>
          </button>
        );
      })}
    </div>
  );
}
